import {Component, OnDestroy, ViewEncapsulation} from '@angular/core';
import {Shared} from "../../shared/shared";
import {AiService} from "../../../service/AiService";
import {ButtonComponent} from "../generic/table/buttonComponent/buttonComponent";
import {AiConfigService} from "../../../service/AiConfigService";
import {CourseService} from "../../../service/CourseService";
import {AiGeneratedQuestionDto} from "../../../dto/AiGeneratedQuestionDto";
import {AiGenerationPlanDto} from "../../../dto/AiGenerationPlanDto";
import {AiGenerationJobDto} from "../../../dto/AiGenerationJobDto";
import {CourseEntity} from "../../../model/CourseEntity";
import {QuestionService} from "../../../service/QuestionService";
import {LibraryService} from "../../../service/LibraryService";
import {CommonQuestionDto} from "../../../dto/CommonQuestionDto";
import * as XLSX from 'xlsx';
import * as fs from 'file-saver';
import * as moment from 'moment-timezone';

/**
 * Preguntas generadas por IA: se piden aqui y se revisan aqui.
 *
 * Las preguntas ya no las genera cada alumno por su cuenta. El gestor pide lo que quiere en la
 * barra de arriba ("dame 10 preguntas de cada tema de Cardiologia"), la peticion se interpreta y
 * se le ensena el plan con el coste ANTES de gastar nada; al confirmar, la generacion corre en
 * segundo plano y va cayendo en la cola de pendientes.
 *
 * La cola solo tiene lo que no ha revisado nadie. Aceptar marca human_reviewed y la pregunta pasa
 * a ser una mas del banco, visible en la pantalla de preguntas normales y servible a cualquier
 * alumno. Rechazar la BORRA, a ella y a sus respuestas.
 *
 * Permisos (ADD) que gobiernan la pantalla:
 *  - AI_QUESTIONS: entrar. Lo mira el menu, igual que el resto de pantallas de magnament.
 *  - AI_QUESTIONS_REVIEW: aceptar y rechazar. Sin el la cola se ve, pero en solo lectura.
 *  - AI_QUESTIONS_GENERATE: pedir preguntas. Va aparte de revisar porque cada peticion gasta
 *    dinero real.
 *  - AI_INDEX: mantenimiento del indice (prompts por defecto, indexar manuales/videos), que son
 *    acciones que tocan la corporacion entera y no solo una pregunta.
 *
 * Lo que mira checkIsPermitted es shared.PERMISSIONS, o sea manager_permissions, que NO es la
 * tabla permissions del catalogo de roles. Mientras el manager no tenga AI_QUESTIONS ahi se
 * entiende que el reparto de permisos de IA aun no ha llegado a su rol y la pantalla no esconde
 * nada: un front por delante de los datos que se auto-bloquea deja una pantalla muerta y sin
 * pistas. En cuanto AI_QUESTIONS aparece en manager_permissions, los tres finos mandan de verdad.
 */
@Component({
  selector: 'app-ai-questions',
  templateUrl: 'aiQuestions.html',
  styleUrls: ['aiQuestions.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AiQuestions implements OnDestroy {

  segment: 'pending' | 'reviewed' = 'pending';
  pending: AiGeneratedQuestionDto[] = [];
  reviewed: AiGeneratedQuestionDto[] = [];
  selected: AiGeneratedQuestionDto = null;
  /**
   * Copia de trabajo mientras se edita. Se edita una copia y no la fila de la lista para que
   * cancelar sea de verdad cancelar: si se tocara el objeto real, salir sin guardar dejaria la
   * tabla y la ficha ensenando algo que no esta en base de datos.
   */
  editando: AiGeneratedQuestionDto = null;
  guardando = false;
  loading = false;
  showTable = true;
  /** Ultimo fallo de carga, para no dejar la pantalla muda cuando el backend no responde. */
  error: string = null;

  // Barra de peticion
  courses: CourseEntity[] = [];
  courseId: number = null;
  requestText = '';
  plan: AiGenerationPlanDto = null;
  planning = false;
  /** Para no pedir el coste en cada pulsacion mientras se ajustan los numeros. */
  private estimarTimeout: any = null;
  launching = false;
  /** De donde sale el material: MANUALES, LIBRE (Claude + internet) o AMBOS. */
  source = 'MANUALES';

  // Contenidos del curso: lo que se desmarca no se usa para generar (tampoco en el chat del alumno)
  contenidosAbiertos = false;
  contenidosCargando = false;
  contenidosGuardando = false;
  /** Agrupados por carpeta, que es como se ven y se marcan. */
  carpetas: { ruta: string, abierta: boolean, items: any[] }[] = [];
  contenidosAviso: string = null;

  // Contenidos adicionales: PDF que solo usa la IA para generar y nunca se citan
  extrasAbiertos = false;
  extras: any[] = [];
  extraTitulo = '';
  extraFichero: File = null;
  extraSubiendo = false;
  extraAviso: string = null;

  // ------------------------------------------------------------------ imagen de la pregunta
  /** Texto libre del revisor: qué cambiar, o qué quiere ver si genera de cero. */
  imagenInstrucciones = '';
  imagenTrabajando = false;
  imagenMensaje: string = null;
  /** Todas las versiones de la imagen. Los retoques se apilan y se puede volver a una anterior. */
  imagenesPrevias: any[] = [];

  jobs: AiGenerationJobDto[] = [];
  /** Los que se pintan. Se calcula al recibirlos y NO en el template: una funcion que devuelve
   *  un array nuevo en cada ciclo de deteccion de cambios hace que *ngFor se repinte sin parar. */
  jobsVisibles: AiGenerationJobDto[] = [];
  private jobsTimer: any = null;

  headers: any = {
    columns: {
      created: {title: 'Fecha', hide: false, filter: false},
      subjectName: {title: 'Asignatura', hide: false, filter: false},
      topicName: {title: 'Tema', hide: false, filter: false},
      keyPoint: {
        title: 'Que evalua', hide: false, filter: false,
        valuePrepareFunction: (keyPoint: string) => {
          return keyPoint == null ? '' : keyPoint;
        }
      },
      statement: {
        title: 'Enunciado', hide: false, filter: false,
        valuePrepareFunction: (statement: string) => {
          return statement == null ? '' : statement.substring(0, 90);
        }
      },
      difficulty: {title: 'Dif.', hide: false, filter: false},
      // Con las casillas encendidas, pinchar la fila ya no abre nada (es de ng2-smart-table), asi
      // que abrir pasa a ser un boton. Se pinta siempre, tambien en la pestana de aceptadas, para
      // que la tabla no se comporte de dos maneras distintas segun donde estes.
      id: {
        title: '', hide: false, filter: false, type: 'custom',
        valuePrepareFunction: (cell, row) => {
          return {cell: row.id, icon: 'open-outline', text: 'Abrir', color: '#1c8aa5'};
        },
        renderComponent: ButtonComponent,
        onComponentInitFunction: (instance) => {
          if (instance != null) {
            instance.changeButton.subscribe(id => this.abrirPorId(id));
          }
        },
      },
      reference: {title: 'Reference', hide: true, filter: false},
      comment: {title: 'Comment', hide: true, filter: false},
      correctAnswer: {title: 'Correct answer', hide: true, filter: false},
      subjectId: {title: 'Subject id', hide: true, filter: false},
      topicId: {title: 'Topic id', hide: true, filter: false},
      answers: {title: 'Answers', hide: true, filter: false},
      processing: {title: '', hide: true, filter: false}
    },
    actions: false,
  };

  constructor(public shared: Shared, private aiService: AiService,
              private aiConfigService: AiConfigService, private courseService: CourseService,
              private questionService: QuestionService, private libraryService: LibraryService) {
    // TEMPORAL: quitar cuando este confirmado que la pantalla monta.
    console.log('[aiQuestions] componente montado');
    this.load();
  }

  ngOnDestroy() {
    this.pararSeguimiento();
  }

  get canReview(): boolean {
    return this.permitido('AI_QUESTIONS_REVIEW');
  }

  get canGenerate(): boolean {
    return this.permitido('AI_QUESTIONS_GENERATE');
  }

  get canIndex(): boolean {
    return this.permitido('AI_INDEX');
  }

  private permitido(permiso: string): boolean {
    return this.shared.checkIsAiPermitted(permiso) === true;
  }

  load() {
    // TEMPORAL: quitar cuando este confirmado que la pantalla monta.
    console.log('[aiQuestions] load() -> ' + this.shared.MAGNAMENT_URL + '/ai/getPendingQuestions/' + this.shared.CORP_ID);
    this.loading = true;
    // Todas las llamadas llevan su rama de error: sin ella, un endpoint que falle deja loading a
    // true para siempre y la pantalla se queda en blanco sin decir por que.
    this.aiService.getPendingQuestions().subscribe(x => {
      this.pending = x == null ? [] : x;
      this.loading = false;
      this.refrescarTabla();
    }, e => {
      this.pending = [];
      this.loading = false;
      this.error = 'No se ha podido cargar la cola de preguntas (' + this.textoError(e) + ').';
    });
    this.aiService.getReviewedQuestions().subscribe(x => {
      this.reviewed = x == null ? [] : x;
    }, () => this.reviewed = []);
    if (this.canGenerate && this.courses.length === 0) {
      this.courseService.getCourses(this.shared.CORP_ID).subscribe(x => {
        this.courses = x == null ? [] : x;
      }, () => this.courses = []);
    }
    this.cargarTrabajos();
  }

  /** Abre la ficha desde el boton de la fila. */
  abrirPorId(id: number) {
    const fila = this.list.find(x => x.id === id);
    if (fila != null) {
      this.rowSelectedEmitter(fila);
    }
  }

  get list(): AiGeneratedQuestionDto[] {
    return this.segment === 'pending' ? this.pending : this.reviewed;
  }

  cambiarSegmento(segmento: 'pending' | 'reviewed') {
    if (!this.confirmarSalidaDeEdicion()) {
      return;
    }
    this.segment = segmento;
    this.selected = null;
    this.refrescarTabla();
  }

  /**
   * ng2-smart-table se queda con la fuente de datos con la que se pinto, asi que para que la
   * tabla refleje un cambio de lista hay que sacarla del DOM y volver a meterla.
   */
  private refrescarTabla() {
    this.showTable = false;
    setTimeout(() => this.showTable = true, 50);
  }

  rowSelectedEmitter(row: any) {
    if (row == null) {
      if (this.confirmarSalidaDeEdicion()) {
        this.selected = null;
      }
      return;
    }
    if (this.editando != null && this.editando.id === row.id) {
      // Es la que se esta editando: no se hace nada, para no tirar el borrador por un clic.
      return;
    }
    if (!this.confirmarSalidaDeEdicion()) {
      return;
    }
    // La fila que emite la tabla es una copia plana, asi que se busca el objeto real: es el que
    // lleva el estado de procesando y el que hay que quitar de la lista al resolverlo.
    this.selected = this.list.find(x => x.id === row.id);
    this.imagenInstrucciones = '';
    this.imagenMensaje = null;
    this.cargarImagenes(this.selected);
  }

  // -------------------------------------------------------------- imagen de la pregunta
  //
  // Una imagen generada no se puede comprobar por código: si la anatomía está inventada o el
  // hallazgo no es el que decide la respuesta, lo único que lo caza es mirarla aquí. Por eso hay
  // cuatro salidas y no una: generar otra, retocar la que hay, subir una propia o quitarla.

  cargarImagenes(q: AiGeneratedQuestionDto) {
    this.imagenesPrevias = [];
    if (q == null || q.id == null) {
      return;
    }
    this.aiConfigService.getQuestionImages(q.id).subscribe(x => this.imagenesPrevias = x == null ? [] : x);
  }

  /** Genera una imagen nueva desde cero. Las instrucciones son opcionales. */
  generarImagen(q: AiGeneratedQuestionDto) {
    if (q == null || this.imagenTrabajando) {
      return;
    }
    this.imagenTrabajando = true;
    this.imagenMensaje = 'Generando la imagen…';
    this.aiConfigService.generateQuestionImage(q.id, this.imagenInstrucciones)
      .subscribe(r => this.trasImagen(q, r), () => this.falloImagen());
  }

  /** Parte de la imagen actual y cambia sólo lo que se pida. Sin instrucciones no tiene sentido. */
  retocarImagen(q: AiGeneratedQuestionDto) {
    if (q == null || this.imagenTrabajando || !this.imagenInstrucciones || q.imageId == null) {
      return;
    }
    this.imagenTrabajando = true;
    this.imagenMensaje = 'Retocando la imagen…';
    this.aiConfigService.editQuestionImage(q.id, q.imageId, this.imagenInstrucciones)
      .subscribe(r => this.trasImagen(q, r), () => this.falloImagen());
  }

  subirImagen(q: AiGeneratedQuestionDto, evento: any) {
    const fichero = evento == null || evento.target == null || evento.target.files == null
      ? null : evento.target.files[0];
    if (q == null || fichero == null) {
      return;
    }
    this.imagenTrabajando = true;
    this.imagenMensaje = 'Subiendo…';
    this.aiConfigService.uploadQuestionImage(q.id, fichero)
      .subscribe(r => {
        this.trasImagen(q, r);
        // Se limpia el input para poder volver a elegir el mismo fichero si hace falta.
        evento.target.value = '';
      }, () => this.falloImagen());
  }

  borrarImagen(q: AiGeneratedQuestionDto) {
    if (q == null || q.imageId == null || this.imagenTrabajando) {
      return;
    }
    if (!confirm('¿Quitar la imagen de esta pregunta?')) {
      return;
    }
    this.imagenTrabajando = true;
    this.aiConfigService.deleteQuestionImage(q.id, q.imageId)
      .subscribe(r => {
        q.imageUrl = null;
        q.imageId = null;
        this.trasImagen(q, r);
      }, () => this.falloImagen());
  }

  /**
   * Vuelve a una versión anterior, sin gastar una llamada al modelo.
   *
   * Tiene que ir al servidor: la pregunta sirve la ÚLTIMA imagen que tiene colgada, así que
   * cambiarlo sólo en pantalla se perdería al recargar. El backend la vuelve a colgar como última
   * y no borra las posteriores, para poder ir y volver.
   */
  usarVersion(q: AiGeneratedQuestionDto, img: any) {
    if (q == null || img == null || this.imagenTrabajando || img.id === q.imageId) {
      return;
    }
    this.imagenTrabajando = true;
    this.aiConfigService.useQuestionImage(q.id, img.id)
      .subscribe(r => this.trasImagen(q, r), () => this.falloImagen());
  }

  private trasImagen(q: AiGeneratedQuestionDto, r: any) {
    this.imagenTrabajando = false;
    this.imagenMensaje = r == null ? null : r.message;
    if (r != null && r.imageUrl) {
      q.imageUrl = r.imageUrl;
    }
    if (r != null && r.images) {
      this.imagenesPrevias = r.images;
      // La última es la que se acaba de crear, y es la que queda asociada a la pregunta.
      if (r.images.length > 0) {
        q.imageId = r.images[r.images.length - 1].id;
      } else {
        q.imageId = null;
        q.imageUrl = null;
      }
    }
    this.imagenInstrucciones = '';
  }

  private falloImagen() {
    this.imagenTrabajando = false;
    this.imagenMensaje = 'No se ha podido. Revisa que haya credencial de OpenAI y mira el log.';
  }

  // ------------------------------------------------------------------------- edicion

  /**
   * Editar antes de resolver.
   *
   * La cola no puede ser solo aceptar o rechazar: casi todo lo que se rechazaba estaba bien salvo
   * por una palabra del enunciado o por un distractor flojo, y rechazarlo obligaba a generar (y a
   * pagar) otra vez lo mismo sin garantia de que saliera mejor.
   *
   * Guardar no acepta la pregunta: se queda donde estaba y hay que resolverla igual.
   */
  editar() {
    if (this.selected == null || !this.canReview) {
      return;
    }
    this.editando = this.copia(this.selected);
  }

  cancelarEdicion() {
    if (!this.confirmarSalidaDeEdicion()) {
      return;
    }
    this.editando = null;
  }

  /**
   * Las opciones son cadenas sueltas, asi que sin trackBy *ngFor las identifica por su propio
   * texto: al escribir una letra la fila pasaria por nueva, Angular rehace el textarea y se
   * pierde el foco en cada pulsacion. Por posicion se queda quieta.
   */
  porIndice(index: number): number {
    return index;
  }

  /** El numero de respuesta que guarda la base de datos empieza en 1, no en 0. */
  marcarCorrecta(index: number) {
    if (this.editando != null) {
      this.editando.correctAnswer = index + 1;
    }
  }

  get puedeGuardar(): boolean {
    const e = this.editando;
    if (e == null || this.guardando) {
      return false;
    }
    if (e.statement == null || e.statement.trim() === '') {
      return false;
    }
    if (e.answers == null || e.answers.length === 0) {
      return false;
    }
    if (e.answers.some(a => a == null || a.trim() === '')) {
      return false;
    }
    return e.correctAnswer >= 1 && e.correctAnswer <= e.answers.length;
  }

  guardarEdicion() {
    if (!this.puedeGuardar) {
      return;
    }
    this.guardando = true;
    // Se manda solo lo que edita esta pantalla, y no la copia entera: processing es de aqui y
    // asignatura, tema y fecha no se tocan desde la revision.
    const cambios: any = {
      id: this.editando.id,
      statement: this.editando.statement,
      answers: this.editando.answers,
      correctAnswer: this.editando.correctAnswer,
      comment: this.editando.comment,
      difficulty: this.editando.difficulty,
      keyPoint: this.editando.keyPoint
    };
    this.aiService.updateQuestion(cambios, this.shared.CURRENT_MANAGER.id).subscribe(r => {
      this.guardando = false;
      if (r != null && r.ok === false) {
        this.error = r.message;
        return;
      }
      this.error = null;
      // Se vuelca lo guardado sobre la fila real de la lista, que es la que pinta la tabla y la
      // que se acabara aceptando o rechazando.
      this.volcar(r != null && r.question != null ? r.question : this.editando, this.selected);
      this.editando = null;
      this.refrescarTabla();
    }, e => {
      this.guardando = false;
      this.error = 'No se han podido guardar los cambios (' + this.textoError(e) + ').';
    });
  }

  /** Copia para editar. Las respuestas van aparte: sin esto se editaria el array de la fila. */
  private copia(q: AiGeneratedQuestionDto): AiGeneratedQuestionDto {
    const c: AiGeneratedQuestionDto = Object.assign(new AiGeneratedQuestionDto(), q);
    c.answers = q.answers == null ? [] : q.answers.slice();
    return c;
  }

  /** Solo lo editable: lo demas (asignatura, tema, fecha) no lo cambia esta pantalla. */
  private volcar(desde: AiGeneratedQuestionDto, hacia: AiGeneratedQuestionDto) {
    if (desde == null || hacia == null) {
      return;
    }
    hacia.statement = desde.statement;
    hacia.answers = desde.answers == null ? [] : desde.answers.slice();
    hacia.correctAnswer = desde.correctAnswer;
    hacia.comment = desde.comment;
    hacia.difficulty = desde.difficulty;
    hacia.keyPoint = desde.keyPoint;
  }

  /**
   * Se llama antes de cualquier cosa que se lleve el borrador por delante. Solo pregunta si hay
   * algo escrito distinto de lo que habia: preguntar por un formulario que no se ha tocado seria
   * un estorbo.
   */
  private confirmarSalidaDeEdicion(): boolean {
    if (this.editando == null || !this.hayCambios()) {
      this.editando = null;
      return true;
    }
    if (!confirm('Hay cambios sin guardar en la pregunta. ¿Los descartas?')) {
      return false;
    }
    this.editando = null;
    return true;
  }

  private hayCambios(): boolean {
    const e = this.editando;
    const o = this.selected;
    if (e == null || o == null) {
      return false;
    }
    const respuestas = o.answers == null ? [] : o.answers;
    const editadas = e.answers == null ? [] : e.answers;
    if (respuestas.length !== editadas.length
      || respuestas.some((a, i) => a !== editadas[i])) {
      return true;
    }
    return e.statement !== o.statement
      || e.correctAnswer !== o.correctAnswer
      || !this.mismoTexto(e.comment, o.comment)
      || Number(e.difficulty) !== Number(o.difficulty)
      || !this.mismoTexto(e.keyPoint, o.keyPoint);
  }

  /**
   * Un campo que llega vacio de base de datos es null, pero el textarea lo devuelve como cadena
   * vacia. Sin esto, abrir y cerrar la edicion sin tocar nada preguntaria por cambios que no hay.
   */
  private mismoTexto(a: string, b: string): boolean {
    return (a == null ? '' : a) === (b == null ? '' : b);
  }


  // ------------------------------------------------------------------------ revision en bloque

  /**
   * Lo marcado ahora mismo en la tabla. Son los mismos objetos que hay en la lista, asi que
   * comparar por id es suficiente para sacarlos de la cola despues.
   */
  seleccion: AiGeneratedQuestionDto[] = [];
  /** Hay una accion en bloque en marcha: los dos botones se apagan mientras tanto. */
  enBloque = false;

  cambiarSeleccion(filas: AiGeneratedQuestionDto[]) {
    this.seleccion = filas == null ? [] : filas;
  }

  get puedeEnBloque(): boolean {
    return this.canReview && this.segment === 'pending' && !this.enBloque
      && this.editando == null && this.seleccion.length > 0;
  }

  aceptarSeleccionadas() {
    if (!this.puedeEnBloque) {
      return;
    }
    const ids = this.seleccion.map(x => x.id);
    this.enBloque = true;
    this.aiService.reviewQuestions(ids, this.shared.CURRENT_MANAGER.id).subscribe(r => {
      this.enBloque = false;
      this.resolverBloque(ids, r, true);
    }, e => {
      this.enBloque = false;
      this.error = 'No se han podido aceptar las preguntas (' + this.textoError(e) + ').';
    });
  }

  rechazarSeleccionadas() {
    if (!this.puedeEnBloque) {
      return;
    }
    const ids = this.seleccion.map(x => x.id);
    if (!confirm('Se van a borrar ' + ids.length + ' preguntas con todo lo que cuelga de ellas. Esto no se puede deshacer.')) {
      return;
    }
    this.enBloque = true;
    this.aiService.discardQuestions(ids, this.shared.CURRENT_MANAGER.id).subscribe(r => {
      this.enBloque = false;
      this.resolverBloque(ids, r, false);
    }, e => {
      this.enBloque = false;
      this.error = 'No se han podido borrar las preguntas (' + this.textoError(e) + ').';
    });
  }

  /**
   * Saca de la cola solo lo que de verdad se ha resuelto.
   *
   * Los ids que el backend devuelve en failed se quedan donde estaban: una fila que desaparece sin
   * haberse borrado es peor que un aviso, porque el revisor la da por hecha.
   */
  private resolverBloque(pedidos: number[], respuesta: any, aceptadas: boolean) {
    const fallidos: number[] = respuesta != null && respuesta.failed != null ? respuesta.failed : [];
    const resueltos = pedidos.filter(id => fallidos.indexOf(id) < 0);
    const movidas = this.pending.filter(x => resueltos.indexOf(x.id) >= 0);

    this.pending = this.pending.filter(x => resueltos.indexOf(x.id) < 0);
    if (aceptadas) {
      this.reviewed = movidas.concat(this.reviewed);
    }
    this.seleccion = [];
    this.selected = null;
    this.error = fallidos.length > 0 && respuesta != null ? respuesta.message : null;
    this.refrescarTabla();
  }

  // ------------------------------------------------------------------------ descarga

  /** Asignaturas con sus temas, para sacar los alias. Se pide una vez, la primera vez que se descarga. */
  private commonQuestion: CommonQuestionDto = null;
  descargando = false;

  /**
   * Descarga en CSV lo seleccionado o, si no hay nada marcado, toda la lista que se esta viendo.
   *
   * Mismas columnas que la exportacion de la pantalla de preguntas, para que el fichero se pueda
   * importar alli (o donde sea) sin tocarlo. Por eso asignatura y tema van por alias y no por
   * nombre: es lo que busca el importador. Lo que una pregunta de IA no tiene (examen oficial,
   * origenes, tags, grupos) sale vacio.
   */
  async descargarCsv() {
    const preguntas = this.seleccion.length > 0 ? this.seleccion : this.list;
    if (preguntas.length === 0 || this.descargando) {
      return;
    }
    this.descargando = true;
    if (this.commonQuestion == null) {
      (await this.questionService.getCommonQuestion()).subscribe(t => {
        this.commonQuestion = t;
        this.escribirCsv(preguntas);
      }, () => this.escribirCsv(preguntas));
    } else {
      this.escribirCsv(preguntas);
    }
  }

  private escribirCsv(preguntas: AiGeneratedQuestionDto[]) {
    const header = [
      'ID', 'STATEMENT', 'N1', 'ANSWER1', 'N2', 'ANSWER2', 'N3', 'ANSWER3', 'N4', 'ANSWER4',
      'N5', 'ANSWER5', 'N6', 'ANSWER6', 'N7', 'ANSWER7', 'N8', 'ANSWER8', 'N9', 'ANSWER9', 'CORRECT',
      'SUBJECT', 'TOPIC', 'COMMENT', 'DIFFICULT', 'OFFICIAL_EXAM', 'OFFICIAL_YEAR',
      'OFFICIAL_NUM', 'ORIGINS', 'TAGS', 'GROUPS', 'REFERENCE'
    ];
    const rows = preguntas.map(q => {
      const answers = q.answers == null ? [] : q.answers;
      const row: any[] = [q.id ?? '', q.statement ?? ''];
      for (let i = 0; i < 9; i++) {
        row.push(...(answers[i] != null ? [i + 1, answers[i]] : ['', '']));
      }
      const subject = this.commonQuestion?.subject?.find(s => s.id === q.subjectId);
      const topic = subject?.list?.find(t => t.id === q.topicId);
      row.push(
        q.correctAnswer ?? '0',
        subject?.alias ?? q.subjectName ?? '',
        topic?.alias ?? q.topicName ?? '',
        q.comment ?? '',
        q.difficulty ?? '',
        '', '', '', '', '', '',
        q.reference ?? ''
      );
      return row;
    });

    // Separado por tabuladores: los enunciados y comentarios van llenos de comas.
    const csv = XLSX.utils.sheet_to_csv(XLSX.utils.aoa_to_sheet([header, ...rows]),
      {FS: String.fromCharCode(9), RS: String.fromCharCode(13, 10)});
    // En UTF-16LE con BOM y no en UTF-8: con un .csv en UTF-8 Excel ignora los tabuladores y
    // parte por el separador regional, y todo acaba en una columna. En UTF-16LE detecta solo que
    // va por tabuladores y respeta las tildes.
    const texto = String.fromCharCode(0xFEFF) + csv;
    const bytes = new Uint8Array(texto.length * 2);
    for (let i = 0; i < texto.length; i++) {
      const c = texto.charCodeAt(i);
      bytes[i * 2] = c & 0xFF;
      bytes[i * 2 + 1] = c >> 8;
    }
    const blob = new Blob([bytes], {type: 'text/csv;charset=utf-16le'});
    fs.saveAs(blob, 'AiQuestions_' + moment().format('yyyyMMDDHHmmss') + '.csv');
    this.descargando = false;
  }

  // ------------------------------------------------------------------------ revision

  approve(q: AiGeneratedQuestionDto) {
    if (q == null || q.processing || !this.canReview || this.editando != null) {
      return;
    }
    q.processing = true;
    this.aiService.reviewQuestion(q.id, this.shared.CURRENT_MANAGER.id).subscribe(() => {
      // Se saca de la cola al momento en vez de recargar: la lista puede ser larga y recargarla
      // entera haria perder la posicion de lectura.
      this.error = null;
      this.pending = this.pending.filter(x => x.id !== q.id);
      q.processing = false;
      this.reviewed.unshift(q);
      this.selected = null;
      this.refrescarTabla();
    }, e => {
      q.processing = false;
      this.error = 'No se ha podido aceptar la pregunta (' + this.textoError(e) + ').';
    });
  }

  discard(q: AiGeneratedQuestionDto) {
    if (q == null || q.processing || !this.canReview || this.editando != null) {
      return;
    }
    if (!confirm('Se va a borrar la pregunta y sus respuestas. Esto no se puede deshacer.')) {
      return;
    }
    q.processing = true;
    this.aiService.discardQuestion(q.id, this.shared.CURRENT_MANAGER.id).subscribe(r => {
      q.processing = false;
      // El servidor puede negarse a borrar (por ejemplo si la pregunta ya se usa en un examen).
      // En ese caso la fila se queda donde esta y se dice por que, en vez de fingir que se fue.
      if (r != null && r.ok === false) {
        this.error = r.message;
        return;
      }
      this.error = null;
      this.pending = this.pending.filter(x => x.id !== q.id);
      this.selected = null;
      this.refrescarTabla();
    }, e => {
      q.processing = false;
      this.error = 'No se ha podido borrar la pregunta (' + this.textoError(e) + ').';
    });
  }

  /**
   * Rotulo de una opcion en la pantalla de revision.
   *
   * Nueve letras y no cinco: hay cursos cuyos simulacros llegan a nueve opciones (los tipo NBME de
   * Costa Rica), y con el alfabeto cortado en la E las opciones de la sexta en adelante salian sin
   * rotulo. Aqui siempre se rotula con letra aunque el alumno vea numeros: es una lista de
   * revision, no el examen.
   */
  letter(index: number): string {
    return 'ABCDEFGHI'.charAt(index);
  }

  isCorrect(q: AiGeneratedQuestionDto, index: number): boolean {
    return q.correctAnswer === index + 1;
  }

  // ------------------------------------------------------------------- peticion de preguntas

  pedirPlan() {
    if (!this.canGenerate || this.planning || this.requestText.trim() === '') {
      return;
    }
    this.planning = true;
    this.plan = null;
    this.aiService.planGeneration(this.courseId, this.requestText).subscribe(x => {
      this.plan = x;
      this.planning = false;
    }, () => {
      this.planning = false;
    });
  }

  /**
   * El gestor puede ajustar a mano cuantas quiere de cada tema antes de confirmar.
   *
   * El total se pinta al momento, pero el precio lo vuelve a decir el backend. Antes se
   * multiplicaba aqui por un numero fijo escrito a pelo, que ademas solo cubria la salida del
   * modelo: en cuanto se tocaba un tema, la cifra dejaba de tener que ver ni con el backend ni con
   * la factura. Y ahora la cuenta lleva dentro las imagenes y el descarte, que no caben en una
   * multiplicacion.
   */
  recalcularTotal() {
    if (this.plan == null || this.plan.items == null) {
      return;
    }
    let total = 0;
    this.plan.items.forEach(x => total += (x.num == null ? 0 : Number(x.num)));
    this.plan.total = total;

    // Se espera a que pare de escribir: cada pulsacion en el input dispara este metodo.
    if (this.estimarTimeout != null) {
      clearTimeout(this.estimarTimeout);
    }
    this.estimarTimeout = setTimeout(() => this.pedirEstimacion(), 400);
  }

  private pedirEstimacion() {
    if (this.plan == null || this.plan.total <= 0) {
      return;
    }
    this.aiService.estimateGenerationCost(this.courseId, this.plan).subscribe(x => {
      // Puede haber seguido tocando numeros mientras iba la peticion: solo se cogen las cifras del
      // coste, nunca los temas, que son los que tiene delante.
      if (x != null && this.plan != null) {
        this.plan.estimatedCostEur = x.estimatedCostEur;
        this.plan.textCostEur = x.textCostEur;
        this.plan.imageCostEur = x.imageCostEur;
        this.plan.imageCount = x.imageCount;
        this.plan.retryFactor = x.retryFactor;
      }
    });
  }

  quitarTema(index: number) {
    this.plan.items.splice(index, 1);
    this.recalcularTotal();
  }

  // ------------------------------------------------------------------ contenidos del curso

  cambioDeCurso() {
    this.carpetas = [];
    this.contenidosAviso = null;
    if (this.contenidosAbiertos) {
      this.cargarContenidos();
    }
  }

  toggleContenidos() {
    this.contenidosAbiertos = !this.contenidosAbiertos;
    if (this.contenidosAbiertos && this.carpetas.length === 0) {
      this.cargarContenidos();
    }
  }

  cargarContenidos() {
    if (this.courseId == null) {
      return;
    }
    this.contenidosCargando = true;
    this.aiService.getCourseContents(this.courseId).subscribe(lista => {
      this.contenidosCargando = false;
      const porRuta = new Map<string, any[]>();
      for (const c of lista || []) {
        const ruta = c.folderPath || 'Sin carpeta';
        if (!porRuta.has(ruta)) {
          porRuta.set(ruta, []);
        }
        porRuta.get(ruta).push(c);
      }
      this.carpetas = Array.from(porRuta.entries()).map(([ruta, items]) => ({ruta, abierta: false, items}));
    }, () => this.contenidosCargando = false);
  }

  /** Una carpeta cuenta como marcada si se usa todo lo que tiene. */
  carpetaUsada(carpeta): boolean {
    return carpeta.items.every(x => !x.excluded);
  }

  carpetaParcial(carpeta): boolean {
    return !this.carpetaUsada(carpeta) && carpeta.items.some(x => !x.excluded);
  }

  toggleCarpeta(carpeta) {
    const usar = !this.carpetaUsada(carpeta);
    carpeta.items.forEach(x => x.excluded = !usar);
  }

  excluidos(): number {
    return this.carpetas.reduce((n, c) => n + c.items.filter(x => x.excluded).length, 0);
  }

  guardarContenidos() {
    this.contenidosGuardando = true;
    const todos = [].concat(...this.carpetas.map(c => c.items));
    this.aiService.saveCourseContents(this.courseId, todos).subscribe(x => {
      this.contenidosGuardando = false;
      this.contenidosAviso = x?.message || 'Guardado';
    }, () => this.contenidosGuardando = false);
  }

  // ---------------------------------------------------------------- contenidos adicionales

  toggleExtras() {
    this.extrasAbiertos = !this.extrasAbiertos;
    if (this.extrasAbiertos) {
      this.cargarExtras();
    }
  }

  cargarExtras() {
    this.aiService.getExtraContents().subscribe(x => this.extras = x || []);
  }

  elegirExtra(evt) {
    this.extraFichero = evt.target.files[0];
    if (this.extraFichero != null && this.extraTitulo.trim() === '') {
      this.extraTitulo = this.extraFichero.name.replace(/\.pdf$/i, '');
    }
  }

  subirExtra() {
    if (this.extraFichero == null || this.extraTitulo.trim() === '' || this.extraSubiendo) {
      return;
    }
    this.extraSubiendo = true;
    this.extraAviso = null;
    this.libraryService.uploadDocument(this.extraFichero).subscribe(subido => {
      if (subido == null || subido.reference == null) {
        this.extraSubiendo = false;
        this.extraAviso = 'No se ha podido subir el fichero';
        return;
      }
      this.aiService.saveExtraContent(this.extraTitulo.trim(), subido.reference).subscribe(x => {
        this.extraSubiendo = false;
        this.extraAviso = x?.message;
        this.extraTitulo = '';
        this.extraFichero = null;
        this.cargarExtras();
      }, () => this.extraSubiendo = false);
    }, () => this.extraSubiendo = false);
  }

  borrarExtra(extra) {
    if (!confirm('¿Quitar "' + extra.title + '" de los contenidos adicionales?')) {
      return;
    }
    this.aiService.deleteExtraContent(extra.id).subscribe(() => this.cargarExtras());
  }

  confirmarPlan() {
    if (!this.canGenerate || this.launching || this.plan == null || this.plan.total <= 0) {
      return;
    }
    // Ha pedido algo que este examen no admite. No se genera otra cosa a sus espaldas: el gestor
    // puede tocar la configuracion del curso, asi que lo que toca es que lo arregle. El backend lo
    // rechaza igualmente; esto es para que el boton no mienta.
    if (this.plan.blocked) {
      return;
    }
    this.launching = true;
    this.plan.source = this.source;
    this.aiService.launchGeneration(this.courseId, this.requestText, this.plan,
      this.shared.CURRENT_MANAGER.id).subscribe(() => {
      this.launching = false;
      this.plan = null;
      this.requestText = '';
      this.cargarTrabajos();
    }, () => {
      this.launching = false;
    });
  }

  cancelarPlan() {
    this.plan = null;
  }

  // ---------------------------------------------------------------------- avance del trabajo

  cargarTrabajos() {
    if (!this.canGenerate) {
      return;
    }
    this.aiService.getGenerationJobs().subscribe(x => {
      this.jobs = x == null ? [] : x;
      this.calcularJobsVisibles();
      // Mientras haya algo en curso se sigue mirando, y al terminar se recarga la cola una vez
      // para ver las preguntas nuevas.
      if (this.jobs.some(j => j.status === 'RUNNING' || j.status === 'PLANNED')) {
        this.arrancarSeguimiento();
      } else if (this.jobsTimer != null) {
        this.pararSeguimiento();
        this.load();
      }
    }, () => {
      // Si el backend todavia no tiene el endpoint de trabajos, la revision tiene que seguir
      // funcionando igual: se deja de preguntar y ya esta.
      this.jobs = [];
      this.calcularJobsVisibles();
      this.pararSeguimiento();
    });
  }

  private arrancarSeguimiento() {
    if (this.jobsTimer != null) {
      return;
    }
    this.jobsTimer = setInterval(() => this.cargarTrabajos(), 5000);
  }

  private pararSeguimiento() {
    if (this.jobsTimer != null) {
      clearInterval(this.jobsTimer);
      this.jobsTimer = null;
    }
  }

  private calcularJobsVisibles() {
    // Solo lo que se esta generando ahora. Un trabajo terminado no aporta nada: lo que produjo ya
    // esta en la cola de pendientes, que es donde hay que mirarlo, y dejar la barra al 10/10 en
    // pantalla solo ocupa sitio y confunde con una peticion nueva.
    this.jobsVisibles = this.jobs.filter(j => j.status === 'RUNNING' || j.status === 'PLANNED');
  }

  progreso(job: AiGenerationJobDto): number {
    if (job == null || job.total == null || job.total === 0) {
      return 0;
    }
    return Math.min(100, Math.round((job.generated * 100) / job.total));
  }

  // ------------------------------------------------------------ mantenimiento del indice

  ingestLibraries() {
    if (!this.canIndex) {
      return;
    }
    this.aiService.ingestLibraries().subscribe(r => this.aviso(r));
  }

  ingestVideos() {
    if (!this.canIndex) {
      return;
    }
    this.aiService.ingestVideos().subscribe(r => this.aviso(r));
  }

  seedPrompts() {
    if (!this.canIndex) {
      return;
    }
    this.aiService.seedPrompts().subscribe(r => this.aviso(r));
  }

  private textoError(e: any): string {
    if (e == null) {
      return 'sin detalle';
    }
    return typeof e === 'string' ? e : (e.status != null ? 'HTTP ' + e.status : 'sin detalle');
  }

  private aviso(r: any) {
    if (r != null && r.message != null) {
      alert(r.message);
    }
  }
}
